import { alpha, type Theme } from '@mui/material/styles'
import { Group, Type } from '@/api'

export function groupTypeLabel(
  type: Type | undefined,
  ts: (k: string) => string
): string {
  switch (type) {
    case Type.Service:
      return ts('agenciesTypeService')
    case Type.Team:
      return ts('agenciesTypeTeam')
    case Type.Office:
      return ts('agenciesTypeOffice')
    case Type.Organization:
      return ts('agenciesTypeOrganization')
    case Type.Undefined:
      return ts('agenciesTypeUndefined')
    default:
      return ts('agenciesTypeUndefined')
  }
}

export const agencyTypeChipSx = (theme: Theme) =>
  ({
    height: 24,
    flexShrink: 0,
    alignSelf: 'center',
    bgcolor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.neutral?.[700] ?? '#374151', 0.45)
        : alpha(theme.palette.grey[500], 0.12),
    color: theme.palette.neutral?.[600] ?? theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    fontWeight: 600,
    '& .MuiChip-label': { px: 1, fontSize: 10, lineHeight: 1.2 },
  } as const)

export type AgencyVisualVariant = 'primary' | 'nested'

export const AGENCY_NESTED_COLOR = '#9D4FCC'

const agencyNestedColor = () => AGENCY_NESTED_COLOR

export const agencyIconSx = (theme: Theme, variant: AgencyVisualVariant) => {
  const color =
    variant === 'nested' ? agencyNestedColor() : theme.palette.primary.main

  return {
    width: 36,
    height: 36,
    borderRadius: '8px',
    bgcolor: alpha(color, 0.12),
    color,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  } as const
}

export const agencyCardHoverSx = (theme: Theme, variant: AgencyVisualVariant) => {
  const color =
    variant === 'nested' ? agencyNestedColor() : theme.palette.primary.main

  return {
    borderColor: color,
    boxShadow: `0 6px 18px ${alpha(color, 0.1)}`,
  } as const
}

export const agencyNestedBadgeSx = (_theme: Theme) =>
  ({
    height: 24,
    flexShrink: 0,
    alignSelf: 'center',
    bgcolor: alpha(AGENCY_NESTED_COLOR, 0.12),
    color: AGENCY_NESTED_COLOR,
    border: `1px solid ${alpha(AGENCY_NESTED_COLOR, 0.35)}`,
    fontWeight: 600,
    '& .MuiChip-label': { px: 1, fontSize: 10, lineHeight: 1.2 },
    '& .MuiChip-icon': {
      color: AGENCY_NESTED_COLOR,
      ml: 0.75,
      mr: -0.25,
    },
  } as const)

export function getAgencySubtitle(
  group: Group,
  groupByGid: Map<string, Group>,
  t: (k: string, opts?: { name?: string }) => string
): { text: string; monospace: boolean } | null {
  if (!group.parent) {
    return null
  }
  const parentName = groupByGid.get(group.parent)?.name
  if (parentName)
    return {
      text: t('agenciesAttachedTo', { name: parentName }),
      monospace: false,
    }
  return { text: t('agenciesAttachedToUnknown'), monospace: false }
}
